import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { right } from '@popperjs/core';
import {
  AccessPermissionService,
  defaultColumn,
  DELETE_RECORD_LABEL,
  grid_widths_map,
  mainModuleName,
  rolesModuleName,
} from '@piqet-angular/shared';
import { MyOrganizationFacade, getRolesAndPermission } from '@piqet-angular/data';
import { Subject } from 'rxjs';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import * as path from 'path';

@Injectable({
  providedIn: 'root',
})
export class ManageUsersDataGridService {
  event: any;
  manageUsersPayload: any;
  Access: any;
  editUserData = new Subject<[any, any, any, any]>();
  userObservable$ = this.editUserData.asObservable();
  deleteUserData = new Subject<[any, any, any]>();
  userDeleteObservable$ = this.deleteUserData.asObservable();
  manualActivateUser = new Subject<[any, any, any]>();
  activateUserObservable$ = this.manualActivateUser.asObservable();
  manualVerifyReviewer = new Subject<[any, any, any]>();
  manualVerifyReviewer$ = this.manualVerifyReviewer.asObservable();

  // Add new subject for user activation
  activateUserSubject = new Subject<[any, any, any]>();
  activateUserPreferencesObservable$ = this.activateUserSubject.asObservable();

  currentRoute: string = '';
  statusCellClassRules = {
    'status-true-outer': function (params: any) {
      return params.value === true;
    },
    'status-false-outer': function (params: any) {
      return params.value === false;
    },
  };

  constructor(
    private myOrganizationFacade: MyOrganizationFacade,
    private swalService: SwalService,
    private accessService: AccessPermissionService
  ) {}

  accessAllPermission(type: string) {
    const access = this.isAccess(type, 'edit') || this.isAccess(type, 'delete');
    return access;
  }

  getManageUsersDataColumn(pathName: any, resAuth: any): ColDef[] {
    this.currentRoute = pathName;
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'FirstName',
      headerName: 'First name',
      field: 'firstName',
      cellClass: 'my-permission-cursor-pointer',
      filter: 'agTextColumnFilter',
      cellRenderer: 'emailRenderer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'LastName',
      headerName: 'Last name',
      field: 'lastName',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agTextColumnFilter',
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Email',
      headerName: 'Email',
      field: 'email',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agTextColumnFilter',
    });

    colDef.push({
      ...defaultColumn,
      colId: 'AccessRoleName',
      headerName: 'Access role',
      field: 'accessRoleName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agTextColumnFilter',
    });

    colDef.push({
      ...defaultColumn,
      colId: 'OrganizationName',
      headerName: 'Organization',
      field: 'organizationName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      cellRenderer: (params: any) => {
        return `<div class="truncated-text" title="${params.value}">${
          params.value ? params.value : ''
        }</div>`;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'UserName',
      headerName: 'Username',
      field: 'userName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agTextColumnFilter',
    });

    // Updated Active column with activation functionality
    colDef.push({
      ...defaultColumn,
      colId: 'IsActive',
      headerName: 'Active',
      field: 'isActive',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      cellRenderer: 'emailRenderer',
      cellRendererParams: {
        from: 'activation',
        activateUser: (data: any) => {
          this.activateUserSubject.next(data);
        },
      },
      cellClassRules: this.statusCellClassRules,
      filter: 'agTextColumnFilter',
    });

    colDef.push({
      ...defaultColumn,
      colId: 'IsActivated',
      headerName: 'Email verified',
      field: 'isActivated',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      cellRenderer: 'emailRenderer',
      cellRendererParams: {
        resendMail: (data: any) => {
          this.myOrganizationFacade.resendConfirmationMail(data);
        },
        verifyMail: (data: any) => {
          this.manualActivateUser.next(data);
        },
      },
      cellClassRules: this.statusCellClassRules,
      filter: 'agTextColumnFilter',
    });
    colDef.push({
      ...defaultColumn,
      colId: 'IsReviewerVerified',
      headerName: 'Reviewer verified',
      field: 'isReviewerVerified',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      cellRenderer: 'emailRenderer',
      cellRendererParams: {
        from: 'reviewer',
        roleCheck: resAuth,
        verifyReviewer: (data: any) => {
          this.manualVerifyReviewer.next(data);
        },
      },
      cellClassRules: this.statusCellClassRules,
      filter: 'agTextColumnFilter',
    });

    colDef.push({
      ...defaultColumn,
      colId: 'IsSSOUser',
      headerName: 'SSO user',
      field: 'isSSOUser',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      cellRenderer: this.statusRenderer,
      cellClassRules: this.statusCellClassRules,
      filter: 'agTextColumnFilter',
    });

    this.accessAllPermission(pathName) &&
      colDef.push({
        ...defaultColumn,
        colId: 'actions',
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        cellClass: 'my-permission-cursor-pointer',
        cellRenderer: 'actionRenderer',
        maxWidth: grid_widths_map.sm_column_xs,
        cellRendererParams: {
          showEdit: this.isAccess(pathName, 'edit'),
          showDelete: this.isAccess(pathName, 'delete'),
          value: 'manageUsers',
          deleteData: (data: any, customName: any) => {
            this.deleteuserConfirmation(
              data.userId,
              '/MyOrganizationUsers/DeleteUser?userId',
              customName
            );
          },
          editData: (data: any, pathName: any) => {
            this.editUserData.next([data.userId, 'Update user', pathName, data.organizationId]);
          },
        },
        lockVisible: true,
        lockPosition: right,
        resizable: false,
      });

    return colDef;
  }

  deleteuserConfirmation(data: any, customType: any, pathName: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteUserData.next([data, customType, pathName]);
      }
    });
  }

  statusRenderer(params: any) {
    return '<span class="status-element">' + `${params.value == true ? 'Yes' : 'No'}` + '</span>';
  }

  isAccess = (pathName: any, type: 'view' | 'edit' | 'create' | 'delete') => {
    return pathName.includes('admin')
      ? this.accessService.hasModulePermission(
          rolesModuleName.AdministratorUser,
          mainModuleName.userManagementModule,
          type
        )
      : this.accessService.hasModulePermission(
          rolesModuleName.myOrganizationManageUser,
          mainModuleName.manageOrganizationModule,
          type
        );
  };
}
